import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import PanditLogo from './PanditLogo';
import { renameChat as apiRenameChat } from '../lib/chats';
import { instantiateAssistant } from '../lib/companions';
import { ASSISTANTS } from '../pages/AssistantsPage';
import { useTheme } from '../contexts/ThemeContext';
import { useUsage } from '../contexts/UsageContext';
import { paidContentRestricted } from '../lib/platform';
import { getMyCredits } from '../lib/credits';
import './Sidebar.css';

// localStorage key for the user's "Personal context" preferences.
// ChatPage reads this and prepends it to outgoing system prompts so
// the answer adapts to the user's preferred name + about-you blurb.
export const PERSONAL_CONTEXT_KEY = 'arthum.personalContext.v1';
function readPersonalContext() {
  try {
    const raw = localStorage.getItem(PERSONAL_CONTEXT_KEY);
    return raw ? JSON.parse(raw) : { name: '', about: '', tone: 'balanced' };
  } catch { return { name: '', about: '', tone: 'balanced' }; }
}

// All sidebar icons in one place — keeps the JSX below readable.
const I = {
  plus: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>,
  chat: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  img:  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>,
  mitras: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="9" cy="8" r="3.2"/><path d="M2.5 21a6.5 6.5 0 0 1 13 0"/><circle cx="17" cy="9" r="2.5"/><path d="M16 21a5 5 0 0 1 5.5-5"/></svg>,
  search: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>,
  library: <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  chatRow: <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: .45 }}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
  assistRow: <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: .55 }}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>,
  close: <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>,
  trash: <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"/></svg>,
  pencil: <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  dots: <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>,
  logout: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  // ── Settings-menu icons (Gemini-style) ────────────────────────────
  gear: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  person: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  gauge: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 14l4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>,
  card: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
  palette: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 2a10 10 0 0 0 0 20 2 2 0 0 0 2-2 1.5 1.5 0 0 1 1.5-1.5h2a4.5 4.5 0 0 0 4.5-4.5A9.5 9.5 0 0 0 12 2z"/></svg>,
  feedback: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
  help: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  chevronRight: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  sun: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/><line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/><line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/><line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/></svg>,
  moon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
};

// Support contact — the same address used elsewhere in the app for
// privacy / data requests. Surfaced via mailto: in the settings menu
// because we don't (yet) have a dedicated in-app feedback form.
const SUPPORT_EMAIL = 'arthumvision@gmail.com';

// PanditLogo handles its own asset selection + theming; no inline src here.

function Sidebar({
  isOpen, isMobile, onMobileClose,
  chats = [],
  companions = [],               // character sessions (aj-companions table)
  activeChatId,
  chatsLoading,
  onNewChat, onSelectChat, onDeleteChat, onClearAllChats, onRenameChat,
  user, signOut,
  onRequestLogin,                // called when an unsigned user taps "Sign in"
  onSubscribe,                   // opens subscription / usage sheet
  libraryImages = [],            // Phase 2 will populate this from saved image-gen runs
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { usage } = useUsage();
  const [activePanel, setActivePanel] = useState(null);   // 'profile' | 'usage' | 'help' | null
  const [searchOpen, setSearchOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [themeSubOpen, setThemeSubOpen] = useState(false);
  // Anchor coords for the floating settings popup. Re-measured each
  // time the menu opens via getBoundingClientRect() so the popup
  // always lines up with the gear icon (which moves between
  // collapsed/hovered sidebar widths).
  const [menuAnchor, setMenuAnchor] = useState(null);
  // Per-row state for the 3-dot menu + inline rename input.
  const [openMenuFor, setOpenMenuFor] = useState(null);    // chatId | null
  const [renamingId, setRenamingId] = useState(null);      // chatId | null
  const [renameDraft, setRenameDraft] = useState('');
  const userMenuRef = useRef(null);
  const gearRef = useRef(null);
  const settingsPopupRef = useRef(null);
  // Open + position the floating settings popup. Anchored just above
  // the gear icon and aligned to its right edge — same vibe as the
  // Gemini screenshot.
  const openSettings = () => {
    const el = gearRef.current || userMenuRef.current;
    if (el) {
      const r = el.getBoundingClientRect();
      setMenuAnchor({ left: r.right + 8, bottom: window.innerHeight - r.top + 8 });
    }
    setUserMenuOpen(true);
  };
  const toggleSettings = () => {
    if (userMenuOpen) setUserMenuOpen(false);
    else openSettings();
  };

  const isImagesRoute = location.pathname === '/image-generator';
  const isAssistRoute = location.pathname === '/assistants';
  // /mitras/:id is the in-character chat surface (kept). The standalone
  // /mitras listing redirects to /assistants in App.js — included here
  // for the brief moment before the redirect resolves so the Characters
  // button doesn't flicker.
  const isMitrasRoute = location.pathname === '/mitras' || location.pathname.startsWith('/mitras/');
  const isCharactersRoute = isAssistRoute || isMitrasRoute;
  const isChatRoute = !isImagesRoute && !isAssistRoute && !isMitrasRoute;
  // Which scope of chats this tab cares about. Image gen doesn't have
  // a chat sidebar — it has its own prompt history list (below).
  const scopeForRoute = isAssistRoute ? 'assistant' : 'chat';

  // ── Image prompt history ──
  // Same localStorage key ImagePage writes to. We don't share state via
  // React (Sidebar and ImagePage aren't parent/child), so we mirror the
  // list here and refresh whenever a custom event fires ('arthum:img-
  // history-changed', dispatched by ImagePage after each Generate / Clear).
  const [imgBatches, setImgBatches] = useState(() => {
    try {
      const raw = localStorage.getItem('arthum.imgHistory.v2');
      return raw ? (JSON.parse(raw) || []) : [];
    } catch { return []; }
  });
  useEffect(() => {
    const reload = () => {
      try {
        const raw = localStorage.getItem('arthum.imgHistory.v2');
        setImgBatches(raw ? (JSON.parse(raw) || []) : []);
      } catch { setImgBatches([]); }
    };
    window.addEventListener('arthum:img-history-changed', reload);
    window.addEventListener('storage', reload);   // cross-tab sync
    return () => {
      window.removeEventListener('arthum:img-history-changed', reload);
      window.removeEventListener('storage', reload);
    };
  }, []);

  // (Recent-prompt rerun via sidebar click was removed — that list is
  // now read-only. ImagePage still listens for arthum:img-rerun from
  // its own ↻ Rerun button.)

  // Scope-filter first, then search-filter. Legacy chats (no scope
  // field) are treated as 'chat' so they don't vanish from old users.
  // Character sessions (aj-companions) are split by origin:
  //   - assistantId-bearing companions = AI-Assistant clones → only
  //     surface under "Assistant chats"
  //   - everything else (custom personas) → surface under "Recent"
  // alongside regular chats. Sort each block by updatedAt desc. We tag
  // each entry with `kind` so the click handler can route to / vs
  // /mitras/<id>.
  const scopedChats = useMemo(() => {
    const reg = chats
      .filter((c) =>
        scopeForRoute === 'assistant'
          ? c.scope === 'assistant'
          : (!c.scope || c.scope === 'chat')
      )
      .map((c) => ({ kind: 'chat', id: c.chatId, ...c }));
    const mits = (companions || [])
      .filter((c) => (c.messageCount || 0) > 0)
      .filter((c) => scopeForRoute === 'assistant' ? !!c.assistantId : !c.assistantId)
      .map((c) => ({
        kind: 'companion',
        id: c.companionId,
        chatId: c.companionId,            // for keys / activeChat highlight
        title: c.name,
        avatarUrl: c.avatarUrl || null,
        archetype: c.archetype || null,
        assistantId: c.assistantId || null,
        updatedAt: c.updatedAt || 0,
      }));
    return [...reg, ...mits].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [chats, companions, scopeForRoute]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return scopedChats;
    return scopedChats.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [scopedChats, query]);

  // Close the per-row menu when clicking elsewhere.
  useEffect(() => {
    if (!openMenuFor) return undefined;
    const onDown = (e) => {
      if (!e.target.closest?.('.sb-chat-menu, .sb-chat-menu-btn')) {
        setOpenMenuFor(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openMenuFor]);

  const startRename = (chat) => {
    setOpenMenuFor(null);
    setRenamingId(chat.chatId);
    setRenameDraft(chat.title || '');
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(''); };
  const commitRename = async (chat) => {
    const newTitle = renameDraft.trim();
    if (!newTitle || newTitle === chat.title) { cancelRename(); return; }
    // Optimistic local update via the App-level callback so the
    // sidebar reflects the change instantly, then fire the PATCH.
    onRenameChat?.(chat.chatId, newTitle);
    cancelRename();
    try {
      await apiRenameChat(chat.chatId, newTitle);
    } catch (err) {
      console.warn('[chat rename]', err.message);
      // Revert by re-querying via the parent — easiest path is to
      // re-call the rename optimistic with the OLD title.
      onRenameChat?.(chat.chatId, chat.title || 'Chat');
    }
  };

  // Settings popup lifecycle. The popup is portaled to <body>, so a
  // simple "click anywhere outside it" listener works — we just check
  // the popup ref and the gear/avatar (so the toggle button doesn't
  // close + reopen on the same tap).
  useEffect(() => {
    if (!userMenuOpen) { setThemeSubOpen(false); return undefined; }
    const onDown = (e) => {
      if (settingsPopupRef.current && settingsPopupRef.current.contains(e.target)) return;
      if (userMenuRef.current && userMenuRef.current.contains(e.target)) return;
      setUserMenuOpen(false);
    };
    // mousedown fires before click, so the gear/avatar click that
    // opened the menu won't be the same event that closes it.
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [userMenuOpen]);

  // Esc closes whichever overlay is on top.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (libraryOpen) setLibraryOpen(false);
      else if (searchOpen) { setSearchOpen(false); setQuery(''); }
      else if (userMenuOpen) setUserMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen, libraryOpen, userMenuOpen]);

  const closeMobileIfNeeded = () => { if (isMobile) onMobileClose?.(); };

  // "+ New chat" behaves differently per route:
  //   - on /assistants, clear the active chat but STAY on /assistants
  //     so the user can pick an assistant to start a fresh thread
  //   - everywhere else, jump back to / for a clean chat
  const handleNew = () => {
    onNewChat?.();
    if (!isAssistRoute) navigate('/');
    closeMobileIfNeeded();
  };
  const goChat = () => { navigate('/'); closeMobileIfNeeded(); };
  const goImages = () => { navigate('/image-generator'); closeMobileIfNeeded(); };
  const goCharacters = () => { navigate('/assistants'); closeMobileIfNeeded(); };
  const pickChat = async (id) => {
    const picked = chats.find((c) => c.chatId === id)
      || (companions || []).find((c) => c.companionId === id);

    // Character session (aj-companions table): straight to /mitras/<id>.
    if (picked?.companionId) {
      if (searchOpen) { setSearchOpen(false); setQuery(''); }
      closeMobileIfNeeded();
      navigate(`/mitras/${encodeURIComponent(picked.companionId)}`);
      return;
    }

    // Assistant chats now open in the CompanionChatPage view (circular
    // avatar header + "Say hi to start") instead of the old ChatPage
    // pill. We look up the AI Assistant definition by id, materialise
    // a Companion row (idempotent via assistantId), and navigate to
    // /mitras/<companionId>. Falls back to the legacy /  ChatPage flow
    // if anything goes wrong (assistant deleted from codebase, network
    // error during instantiate, etc.) so the click never feels dead.
    if (picked?.scope === 'assistant' && picked.assistantId) {
      const def = ASSISTANTS.find((a) => a.id === picked.assistantId);
      if (def) {
        try {
          const c = await instantiateAssistant(def);
          if (c?.companionId) {
            if (searchOpen) { setSearchOpen(false); setQuery(''); }
            closeMobileIfNeeded();
            navigate(`/mitras/${encodeURIComponent(c.companionId)}`);
            return;
          }
        } catch (err) {
          console.warn('[sidebar assistant → companion]', err.message);
          // Fall through to the legacy ChatPage path.
        }
      }
    }

    onSelectChat(id);
    navigate('/');
    if (searchOpen) { setSearchOpen(false); setQuery(''); }
    closeMobileIfNeeded();
  };

  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Account';
  const planLabel = user?.provider || 'free';

  return (
    <aside className={`sidebar ${isMobile && isOpen ? 'mobile-open' : ''} ${isImagesRoute ? 'is-image-route expanded' : ''}`}>

      {/* ── Search overlay ── */}
      {searchOpen && (
        <div className="sb-search-overlay open">
          <div className="sb-search-input-wrap">
            {I.search}
            <input
              autoFocus
              className="sb-search-input"
              placeholder="Search chats…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button className="sb-search-close" onClick={() => { setSearchOpen(false); setQuery(''); }}>
              {I.close}
            </button>
          </div>
          <div className="sb-search-results">
            {filtered.length === 0 ? (
              <div className="sb-search-empty">
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" style={{ opacity: .3 }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                {query ? 'No matches' : 'Type to search your chats'}
              </div>
            ) : (
              filtered.map((c) => (
                <div
                  key={c.chatId}
                  className={`sb-chat ${activeChatId === c.chatId ? 'on' : ''}`}
                  onClick={() => pickChat(c.chatId)}
                >
                  {I.chatRow}
                  <span className="sb-chat-txt">{c.title || 'New chat'}</span>
                </div>
              ))
            )}
          </div>
          <div className="sb-foot" style={{ flexShrink: 0 }}>
            <div className="sb-user">
              <div className="sb-avatar">
                {user?.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : initial}
              </div>
              <div className="sb-user-info">
                <div className="sb-uname">{displayName}</div>
                <div className="sb-uplan">{planLabel}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Library overlay ── */}
      {libraryOpen && (
        <div className="sb-library-overlay open">
          <div className="sb-library-header">
            <span className="sb-library-title">Library</span>
            <button className="sb-search-close" onClick={() => setLibraryOpen(false)}>
              {I.close}
            </button>
          </div>
          <div className="sb-library-grid">
            {libraryImages.length === 0 ? (
              <div className="sb-lib-empty">
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" style={{ opacity: .3 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                </svg>
                No images yet.<br />Create images to see them here.
              </div>
            ) : (
              libraryImages.map((img, i) => (
                <div key={i} className="sb-lib-card">
                  <img src={img.url} alt={img.prompt || ''} />
                </div>
              ))
            )}
          </div>
          <div className="sb-foot" style={{ flexShrink: 0 }}>
            <div className="sb-user">
              <div className="sb-avatar">
                {user?.picture ? <img src={user.picture} alt="" referrerPolicy="no-referrer" /> : initial}
              </div>
              <div className="sb-user-info">
                <div className="sb-uname">{displayName}</div>
                <div className="sb-uplan">{planLabel}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Top: logo + nav ── */}
      <div className="sb-top">
        <div className="logo-row" onClick={handleNew}>
          <PanditLogo size="sm" className="logo-img pandit-logo-bob" alt="Sutrum AI" />
          <span className="logo-name">Sutrum AI</span>
        </div>
      </div>

      {/* Scrollable middle — everything between the logo and the user
          account row lives here. With many sections (Recent prompts +
          nav + filters + chat history) the sidebar was overflowing
          and clipping content. One unified scroll area + visible
          scrollbar makes the overflow obvious and reachable. */}
      <div className="sb-scroll">

      <div className="sb-nav">
        <button className="sb-new" onClick={handleNew} title="New chat">
          {I.plus}
          <span className="sb-label">New chat</span>
        </button>
        <button className={`sb-item ${isChatRoute ? 'on' : ''}`} onClick={goChat} title="Chat">
          {I.chat}
          <span className="sb-label">Chat</span>
        </button>
        <button className={`sb-item ${isImagesRoute ? 'on' : ''}`} onClick={goImages} title="Create images">
          {I.img}
          <span className="sb-label">Create images</span>
        </button>
        <button className={`sb-item ${isCharactersRoute ? 'on' : ''}`} onClick={goCharacters} title="Characters">
          {I.mitras}
          <span className="sb-label">Characters</span>
        </button>
        <button className="sb-item" onClick={() => setSearchOpen(true)} title="Search chats">
          {I.search}
          <span className="sb-label">Search chats</span>
        </button>
      </div>

      {/* Image-gen filters — ImagePage portals its filter UI here when
          the user is on /image-generator. Visibility is now controlled
          entirely via CSS (.is-image-route on .sidebar + hover/expand
          rules) so that the collapsed icon-only state doesn't reserve
          empty space for filter labels that aren't drawn. */}
      <div id="sb-img-filters-slot" className="sb-img-filters" />

      {/* Recent chats list — shown on chat (/) and assistants (/assistants).
          On /image-generator the chat history is hidden (the page has its
          own "Image history" section). Mitras has its own roster. */}
      {!isMitrasRoute && !isImagesRoute && (
        <div className="sb-history">
          <div className="sb-sect sb-sect-row">
            <span>{isAssistRoute ? 'Character chats' : 'Recent'}</span>
            {scopedChats.length > 0 && (
              <button
                type="button"
                className="sb-sect-action sb-label"
                title={isAssistRoute ? 'Clear all character chats' : 'Clear all chats'}
                onClick={() => {
                  const msg = isAssistRoute
                    ? `Delete all ${scopedChats.length} character chat${scopedChats.length === 1 ? '' : 's'}? This cannot be undone.`
                    : `Delete all ${scopedChats.length} chat${scopedChats.length === 1 ? '' : 's'}? This cannot be undone.`;
                  if (window.confirm(msg)) {
                    onClearAllChats?.(scopeForRoute);
                  }
                }}
              >
                Clear all
              </button>
            )}
          </div>
          <div className="sb-list">
            {chatsLoading && scopedChats.length === 0 && (
              <div className="sb-chat" style={{ opacity: .5, cursor: 'default' }}>
                <span className="sb-chat-txt sb-label">Loading…</span>
              </div>
            )}
            {!chatsLoading && scopedChats.length === 0 && (
              <div className="sb-chat" style={{ opacity: .5, cursor: 'default' }}>
                <span className="sb-chat-txt sb-label">
                  {isAssistRoute ? 'Pick a character to start' : 'No chats yet'}
                </span>
              </div>
            )}
            {scopedChats.map((c) => {
              const isRenaming = renamingId === c.chatId;
              const isMenuOpen = openMenuFor === c.chatId;
              return (
                <div
                  key={c.chatId}
                  className={`sb-chat ${activeChatId === c.chatId ? 'on' : ''}`}
                  onClick={() => !isRenaming && pickChat(c.chatId)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (c.kind !== 'companion') startRename(c);
                  }}
                  title={isRenaming ? '' : (c.title || 'New chat')}
                >
                  {c.kind === 'companion'
                    ? I.mitras
                    : (c.scope === 'assistant' ? I.assistRow : I.chatRow)}

                  {isRenaming ? (
                    <input
                      autoFocus
                      className="sb-chat-rename sb-label"
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitRename(c);
                        if (e.key === 'Escape') cancelRename();
                      }}
                      onBlur={() => commitRename(c)}
                      maxLength={60}
                    />
                  ) : (
                    <span className="sb-chat-txt sb-label">
                      {c.title || 'New chat'}
                    </span>
                  )}

                  {/* Companion entries are managed from the Characters
                      roster (rename/delete live there) — hide the
                      sidebar menu so we don't half-implement rename/
                      delete against the wrong DDB table. */}
                  {!isRenaming && c.kind !== 'companion' && (
                    <div className="sb-chat-menu-wrap sb-label" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="sb-chat-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuFor(isMenuOpen ? null : c.chatId);
                        }}
                        aria-label="More options"
                      >
                        {I.dots}
                      </button>
                      {isMenuOpen && (
                        <div className="sb-chat-menu" role="menu">
                          <button onClick={() => startRename(c)} role="menuitem">
                            {I.pencil} Rename
                          </button>
                          <button
                            className="sb-chat-menu-danger"
                            onClick={() => {
                              setOpenMenuFor(null);
                              onDeleteChat?.(c.chatId);
                            }}
                            role="menuitem"
                          >
                            {I.trash} Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Image-generator: Image history (the list of prompts the user
          has run). Read-only — users see what they generated; if they
          want to rerun they use the ↻ button on the batch card itself.
          Hidden on other routes. */}
      {isImagesRoute && (
        <div className="sb-history sb-history-prompts-bottom">
          <div className="sb-sect">Image history</div>
          <div className="sb-list">
            {imgBatches.length === 0 && (
              <div className="sb-chat" style={{ opacity: .5, cursor: 'default' }}>
                <span className="sb-chat-txt sb-label">Your image history will appear here</span>
              </div>
            )}
            {imgBatches.map((b) => (
              <div
                key={b.id}
                className="sb-chat"
                title={b.prompt}
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('arthum:img-rerun', { detail: b }));
                  closeMobileIfNeeded();
                }}
              >
                <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, opacity: .45 }}>
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                <span className="sb-chat-txt sb-label">{b.prompt}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      </div>{/* /.sb-scroll */}

      {/* ── Footer user row ── */}
      <div className="sb-foot">
        {!user ? (
          // Unsigned state: a single "Sign in" CTA in place of the
          // avatar/menu. The expanded sidebar shows the full label;
          // collapsed sidebar shows just the icon.
          <button
            className="sb-signin"
            onClick={() => { onRequestLogin?.(); closeMobileIfNeeded(); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
              <polyline points="10 17 15 12 10 7" />
              <line x1="15" y1="12" x2="3" y2="12" />
            </svg>
            <span className="sb-label">Sign in</span>
          </button>
        ) : (
        <div ref={userMenuRef} className="sb-user-row" style={{ position: 'relative' }}>
          {/* User account chip — clicking the chip opens the menu too,
              so the whole region behaves like the Gemini account block. */}
          <button
            className="sb-user"
            onClick={toggleSettings}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label={user ? `${displayName}, open settings` : 'Account'}
          >
            <div className="sb-avatar">
              {user?.picture ? (
                <img src={user.picture} alt="" referrerPolicy="no-referrer" />
              ) : initial}
            </div>
            <div className="sb-user-info">
              <div className="sb-uname">{displayName}</div>
              <div className="sb-uplan">{user?.email || planLabel}</div>
            </div>
          </button>
          {/* Settings gear — always visible (no `sb-label` class) so
              users can find the menu even on a collapsed sidebar. This
              was the missing affordance behind the "no option to log
              out" complaint: the previous logout icon collapsed away
              when the sidebar was iconified. */}
          <button
            ref={gearRef}
            type="button"
            className="sb-user-gear"
            onClick={(e) => { e.stopPropagation(); toggleSettings(); }}
            aria-label="Open settings"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title="Settings"
          >
            {I.gear}
          </button>
        </div>
        )}
        {/* Floating settings popup — portaled to <body> so the
            sidebar's `overflow: hidden` can't clip it. Anchored above
            and to the right of the gear icon (Gemini-style). No
            backdrop dim; click outside or Esc closes. */}
        {userMenuOpen && user && createPortal(
          <div
            ref={settingsPopupRef}
            className="sb-settings-popup"
            role="menu"
            aria-label="Settings"
            style={{
              left: menuAnchor?.left ?? 80,
              bottom: menuAnchor?.bottom ?? 80,
            }}
          >
            <div className="sb-settings-modal-head">
              <div>
                <div className="sb-settings-head-name">{displayName}</div>
                <div className="sb-settings-head-email">{user.email}</div>
              </div>
            </div>

              <button
                role="menuitem"
                className="sb-settings-item"
                onClick={() => { setUserMenuOpen(false); setActivePanel('profile'); }}
              >
                {I.person}<span className="sb-settings-label">Personal context</span>
              </button>
              <button
                role="menuitem"
                className="sb-settings-item"
                onClick={() => { setUserMenuOpen(false); setActivePanel('usage'); }}
              >
                {I.gauge}<span className="sb-settings-label">Usage limits</span>
              </button>
              <button
                role="menuitem"
                className="sb-settings-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/assistants');
                  closeMobileIfNeeded();
                }}
              >
                {I.mitras}<span className="sb-settings-label">Characters</span>
              </button>
              <button
                role="menuitem"
                className="sb-settings-item"
                onClick={() => { setUserMenuOpen(false); onSubscribe?.(); }}
              >
                {I.card}<span className="sb-settings-label">Manage subscription</span>
              </button>

              {/* Theme — expandable submenu with Light / Dark options. */}
              <div className={`sb-settings-sub ${themeSubOpen ? 'is-open' : ''}`}>
                <button
                  role="menuitem"
                  className="sb-settings-item"
                  aria-haspopup="menu"
                  aria-expanded={themeSubOpen}
                  onClick={() => setThemeSubOpen((v) => !v)}
                >
                  {I.palette}<span className="sb-settings-label">Theme</span>
                  <span className="sb-settings-chev">{I.chevronRight}</span>
                </button>
                {themeSubOpen && (
                  <div className="sb-settings-submenu">
                    <button
                      role="menuitemradio"
                      aria-checked={theme === 'light'}
                      className={`sb-settings-item ${theme === 'light' ? 'is-on' : ''}`}
                      onClick={() => { setTheme('light'); setThemeSubOpen(false); setUserMenuOpen(false); }}
                    >
                      {I.sun}<span className="sb-settings-label">Light</span>
                      {theme === 'light' && <span className="sb-settings-check">{I.check}</span>}
                    </button>
                    <button
                      role="menuitemradio"
                      aria-checked={theme === 'dark'}
                      className={`sb-settings-item ${theme === 'dark' ? 'is-on' : ''}`}
                      onClick={() => { setTheme('dark'); setThemeSubOpen(false); setUserMenuOpen(false); }}
                    >
                      {I.moon}<span className="sb-settings-label">Dark</span>
                      {theme === 'dark' && <span className="sb-settings-check">{I.check}</span>}
                    </button>
                  </div>
                )}
              </div>

              <a
                role="menuitem"
                className="sb-settings-item"
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Sutrum AI feedback')}`}
                onClick={() => setUserMenuOpen(false)}
              >
                {I.feedback}<span className="sb-settings-label">Send feedback</span>
              </a>
              <button
                role="menuitem"
                className="sb-settings-item"
                onClick={() => { setUserMenuOpen(false); setActivePanel('help'); }}
              >
                {I.help}<span className="sb-settings-label">Help</span>
              </button>

              <div className="sb-settings-divider" />

              <button
                role="menuitem"
                className="sb-settings-item sb-settings-signout"
                onClick={async () => { setUserMenuOpen(false); await signOut?.(); }}
              >
                {I.logout}<span className="sb-settings-label">Sign out</span>
              </button>
          </div>,
          document.body
        )}
        {activePanel && user && createPortal(
          <SettingsPanel
            which={activePanel}
            user={user}
            displayName={displayName}
            usage={usage}
            onSubscribe={onSubscribe}
            onClose={() => setActivePanel(null)}
          />,
          document.body
        )}
      </div>
    </aside>
  );
}

// ── Panel: shared modal frame ──────────────────────────────────────
// Centered overlay with a backdrop + close button. The body is
// branch-rendered based on `which`. Each branch is small enough to
// inline; pulled into a component so the portal block stays readable.
function SettingsPanel({ which, user, displayName, usage, onSubscribe, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const title = which === 'profile' ? 'Personal context'
              : which === 'usage'   ? 'Your usage'
              : 'Help';

  return (
    <div
      className="sb-panel-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="sb-panel">
        <header className="sb-panel-head">
          <h2 className="sb-panel-title">{title}</h2>
          <button type="button" className="sb-panel-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </header>
        <div className="sb-panel-body">
          {which === 'profile' && <ProfilePanelBody user={user} displayName={displayName} onClose={onClose} />}
          {which === 'usage'   && <UsagePanelBody usage={usage} onSubscribe={onSubscribe} onClose={onClose} />}
          {which === 'help'    && <HelpPanelBody />}
        </div>
      </div>
    </div>
  );
}

// ── Profile / Personal context ─────────────────────────────────────
// Saves to localStorage under PERSONAL_CONTEXT_KEY. ChatPage reads
// this key and prepends a one-line preamble to outgoing system
// prompts, so the answer adapts to the user's preferred name +
// about-you blurb and tone.
function ProfilePanelBody({ user, displayName, onClose }) {
  const init = readPersonalContext();
  const [name, setName] = useState(init.name || displayName || '');
  const [about, setAbout] = useState(init.about || '');
  const [tone, setTone] = useState(init.tone || 'balanced');
  const [saved, setSaved] = useState(false);

  const save = () => {
    const next = { name: name.trim(), about: about.trim(), tone };
    try { localStorage.setItem(PERSONAL_CONTEXT_KEY, JSON.stringify(next)); } catch {}
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 900);
  };

  const clear = () => {
    setName(''); setAbout(''); setTone('balanced');
    try { localStorage.removeItem(PERSONAL_CONTEXT_KEY); } catch {}
  };

  return (
    <>
      <p className="sb-panel-intro">
        Set a few things about you and how you'd like AI to reply.
        Applied to all new chats — your characters keep their own
        memory on top of this.
      </p>

      <div className="sb-panel-readonly">
        <div className="sb-panel-row">
          <span className="sb-panel-row-label">Signed in as</span>
          <span className="sb-panel-row-value">{user.email}</span>
        </div>
      </div>

      <label className="sb-panel-field">
        <span>Preferred name</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="What should we call you?"
          maxLength={40}
        />
      </label>

      <label className="sb-panel-field">
        <span>About you</span>
        <textarea
          value={about}
          onChange={(e) => setAbout(e.target.value)}
          placeholder="A line or two about you — interests, role, anything that helps the model give better answers."
          maxLength={500}
          rows={3}
        />
        <span className="sb-panel-counter">{about.length} / 500</span>
      </label>

      <div className="sb-panel-field">
        <span>Tone</span>
        <div className="sb-panel-radios">
          {[
            { id: 'concise', label: 'Concise' },
            { id: 'balanced', label: 'Balanced' },
            { id: 'detailed', label: 'Detailed' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`sb-panel-radio ${tone === opt.id ? 'on' : ''}`}
              onClick={() => setTone(opt.id)}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      <div className="sb-panel-actions">
        <button type="button" className="sb-panel-btn sb-panel-btn-ghost" onClick={clear}>Reset</button>
        <button type="button" className="sb-panel-btn sb-panel-btn-primary" onClick={save}>
          {saved ? 'Saved ✓' : 'Save'}
        </button>
      </div>
    </>
  );
}

// ── Usage panel ────────────────────────────────────────────────────
// Reads the live snapshot from UsageContext (same source UsageMeter
// uses in the header). Mirrors the meter's progress bars + reset
// timers so this entry-point isn't a stub.
function UsagePanelBody({ usage, onSubscribe, onClose }) {
  // Fetch admin-granted bonus credits when this panel opens. Signed-in
  // users only — anon has no credit ledger. Best-effort, silent on
  // failure so the primary usage view still renders. Kept small so
  // we're not shipping a whole "credits" screen just for a balance.
  const [credits, setCredits] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!usage || usage.plan === 'anon') return undefined;
    getMyCredits().then((c) => { if (!cancelled) setCredits(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, [usage?.plan]);

  if (!usage || !usage.limits) {
    return <p className="sb-panel-intro">Loading usage…</p>;
  }
  const lim = usage.limits;
  const planLabel = usage.plan === 'pro' ? 'Pro'
                  : usage.plan === 'anon' ? 'Guest' : 'Free';
  const fmt = (n) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
                  : n >= 1_000 ? `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`
                  : String(n);
  const fmtDuration = (ms) => {
    const s = Math.max(0, Math.floor(ms / 1000));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };
  const Bar = ({ used, max, isCount }) => {
    const pct = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
    const tone = pct >= 95 ? 'crit' : pct >= 80 ? 'warn' : 'ok';
    const txt = isCount ? `${used} / ${max}` : `${fmt(used)} / ${fmt(max)}`;
    return (
      <div className="sb-panel-bar-wrap">
        <div className="sb-panel-bar-head">
          <span>{txt}</span>
        </div>
        <div className={`sb-panel-bar sb-panel-bar-${tone}`}>
          <div style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };
  const now = Date.now();
  return (
    <>
      <div className="sb-panel-plan-row">
        <span>Current plan</span>
        <span className={`sb-panel-plan sb-panel-plan-${usage.plan}`}>{planLabel}</span>
      </div>
      <div className="sb-panel-field">
        <span>Tokens today</span>
        <Bar used={usage.tokensToday || 0} max={lim.tokensDay} />
      </div>
      <div className="sb-panel-field">
        <span>Tokens this month</span>
        <Bar used={usage.tokensMonth || 0} max={lim.tokensMonth} />
      </div>
      <div className="sb-panel-field">
        <span>Images today</span>
        <Bar used={usage.imagesToday || 0} max={lim.imagesDay} isCount />
      </div>
      <div className="sb-panel-field">
        <span>Images this month</span>
        <Bar used={usage.imagesMonth || 0} max={lim.imagesMonth} isCount />
      </div>
      {/* Bonus credits row — only rendered when the user actually
          has admin-granted balance, so free users without any bonus
          don't see a confusing "0 tokens" line. */}
      {credits && credits.balance > 0 && (
        <div className="sb-panel-credits">
          <div className="sb-panel-credits-head">
            <span>Bonus credits</span>
            <strong>{credits.balance.toLocaleString()} tokens</strong>
          </div>
          <p className="sb-panel-credits-hint">
            Bonus tokens are used automatically after your free daily quota.
          </p>
        </div>
      )}
      <p className="sb-panel-reset">
        Daily resets in {fmtDuration(usage.dailyResetMs - now)} ·
        Monthly resets in {fmtDuration(usage.monthlyResetMs - now)}
      </p>
      {usage.plan !== 'pro' && (() => {
        // iOS App Store rejects builds that reference paid content
        // unless the matching IAP has been submitted. Until our IAPs
        // are wired through StoreKit, hide the paid CTA on iOS.
        // Anon → Free upgrade is fine to keep (no purchase involved).
        const isAnon = usage.plan === 'anon';
        if (!isAnon && paidContentRestricted()) return null;
        return (
          <div className="sb-panel-actions">
            <button
              type="button"
              className="sb-panel-btn sb-panel-btn-primary"
              onClick={() => { onClose(); onSubscribe?.(); }}
            >
              {isAnon ? 'Sign in for 10× limits' : 'Upgrade to Pro · $0.7/day'}
            </button>
          </div>
        );
      })()}
    </>
  );
}

// ── Help panel ─────────────────────────────────────────────────────
// FAQ + privacy link + contact email. Privacy link opens the same
// static page LoginModal embeds.
function HelpPanelBody() {
  const QA = [
    { q: 'How do limits work?',
      a: 'Guest visits get 5k tokens & 3 images per day. Signing in (free) bumps that to 50k tokens & 20 images per day. Pro lifts it 10× again.' },
    { q: 'Why do my characters remember things?',
      a: 'Each character keeps its own per-conversation memory. You can review or wipe it from the character chat page — tap the brain icon in the chat header.' },
    { q: 'Why does the theme switch on its own?',
      a: 'Sutrum AI auto-themes based on the time of day in India (6 AM–6 PM = Light, otherwise Dark). Pick a theme manually here in Settings → Theme to override until the next boundary.' },
    { q: 'How do I delete my account?',
      a: 'Email us at ' + SUPPORT_EMAIL + ' from your registered address and we will wipe your account within 7 days.' },
  ];
  return (
    <>
      <p className="sb-panel-intro">Quick answers to common questions.</p>
      <div className="sb-panel-faq">
        {QA.map((item, i) => (
          <details key={i} className="sb-panel-faq-item">
            <summary>{item.q}</summary>
            <p>{item.a}</p>
          </details>
        ))}
      </div>
      <div className="sb-panel-actions sb-panel-actions-stacked">
        <a className="sb-panel-btn sb-panel-btn-ghost" href="/privacy.html" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        <a className="sb-panel-btn sb-panel-btn-primary" href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Sutrum AI help')}`}>
          Contact support
        </a>
      </div>
    </>
  );
}

export default Sidebar;
